import { useState, useEffect } from 'react'
import { X, Gift, Lock } from 'lucide-react'
import { supabase } from '../lib/supabase'

const CURRENCY_SYMBOLS = { NGN:'₦', USD:'$', GBP:'£', EUR:'€', GHS:'₵', KES:'KSh', ZAR:'R', UGX:'USh', TZS:'TSh' }

// Guest-facing: send a cash gift. Calls gift-init, then redirects to the provider's
// hosted page. On return, WeddingMiniSite verifies via the ?gift=<ref> param.
export default function CashGiftModal({ weddingId, itemId = null, fundName, currency = 'NGN', onClose }) {
  const sym = CURRENCY_SYMBOLS[currency] || '₦'
  const minGift = currency === 'NGN' ? 2000 : 5
  const [amount, setAmount]   = useState('')
  const [name, setName]       = useState('')
  const [email, setEmail]     = useState('')
  const [message, setMessage] = useState('')
  const [busy, setBusy]       = useState(false)
  const [error, setError]     = useState('')
  const [pct, setPct]         = useState(0.04)   // fee config (kept in sync with admin_settings)
  const [cap, setCap]         = useState(5000)
  const [floor, setFloor]     = useState(0)

  // Pull the live fee config so the breakdown matches what the server will charge.
  useEffect(() => {
    supabase.from('admin_settings').select('value').eq('key', 'gift_fee').maybeSingle()
      .then(({ data }) => {
        const p = Number(data?.value?.pct)
        const c = Number(data?.value?.cap)
        const f = Number(data?.value?.floor)
        if (p >= 0 && p <= 50) setPct(p / 100)
        setCap(c > 0 ? c : Infinity)   // cap = 0 (or unset→default) handled here
        if (f > 0) setFloor(f)
      })
      .catch(() => {})
  }, [])

  const amt   = Number(amount) || 0
  let fee     = Math.min(Math.round(amt * pct), cap === Infinity ? Infinity : Math.round(cap))
  if (floor > 0) fee = Math.max(fee, Math.round(floor))
  const total = amt + fee

  async function pay() {
    if (!(amt >= minGift)) { setError(`The minimum cash gift is ${sym}${minGift.toLocaleString()}.`); return }
    if (!name.trim()) { setError('Please add your name so the couple can thank you.'); return }
    setBusy(true); setError('')
    try {
      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/gift-init`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', apikey: import.meta.env.VITE_SUPABASE_ANON_KEY, Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}` },
        body: JSON.stringify({ wedding_id: weddingId, item_id: itemId, amount: amt, guest_name: name.trim(), guest_email: email.trim() || null, message: message.trim() || null, origin: window.location.origin }),
      })
      const j = await res.json()
      if (!j.ok || !j.authorization_url) { setError(j.error || 'Could not start the payment.'); setBusy(false); return }
      window.location.href = j.authorization_url
    } catch (_) { setError('Something went wrong. Please try again.'); setBusy(false) }
  }

  const inp = { width: '100%', boxSizing: 'border-box', fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.9rem', padding: '0.65rem 0.8rem', border: '1.5px solid #E5E0D8', borderRadius: '0.7rem', outline: 'none' }
  const ngn = n => `${sym}${Number(n).toLocaleString()}`

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', backdropFilter: 'blur(4px)' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: 'white', borderRadius: '1.25rem', width: '100%', maxWidth: 420, padding: '1.5rem', position: 'relative', maxHeight: '92vh', overflowY: 'auto' }}>
        <button onClick={onClose} style={{ position: 'absolute', top: 12, right: 12, background: 'none', border: 'none', cursor: 'pointer', color: '#BCBCBC' }}><X size={18} /></button>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
          <Gift size={18} color="#C4826A" />
          <h3 style={{ fontFamily: 'Gibs, serif', fontWeight: 400, fontSize: '1.2rem', color: '#1A1A1A', margin: 0 }}>Send a cash gift</h3>
        </div>
        <p style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.78rem', color: '#9B9B9B', margin: '0 0 1.125rem' }}>
          {fundName ? `Towards "${fundName}"` : 'Bless the couple with a gift they can use.'}
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <div style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', fontFamily: 'Gibs, serif', fontSize: '1rem', color: '#6B6B6B' }}>{sym}</span>
            <input value={amount} onChange={e => setAmount(e.target.value.replace(/\D/g, ''))} inputMode="numeric" placeholder="Amount" style={{ ...inp, paddingLeft: '1.6rem', fontSize: '1.05rem' }} />
          </div>
          <input value={name} onChange={e => setName(e.target.value)} placeholder="Your name" style={inp} />
          <input value={email} onChange={e => setEmail(e.target.value)} type="email" placeholder="Your email (optional, for a receipt)" style={inp} />
          <textarea value={message} onChange={e => setMessage(e.target.value)} rows={2} placeholder="A short message for the couple (optional)" style={{ ...inp, resize: 'vertical' }} />

          {/* Live, fully-transparent breakdown — no hidden deductions, the couple
              always receives the full gift amount; a small capped fee is added on top. */}
          {amt > 0 && (
            <div style={{ background: '#FBF6F1', border: '1px solid #ECE2D8', borderRadius: '0.75rem', padding: '0.75rem 0.875rem', display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.82rem', color: '#6B6B6B' }}>
                <span>Your gift</span><span style={{ fontWeight: 700, color: '#1A1A1A' }}>{ngn(amt)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.82rem', color: '#6B6B6B' }}>
                <span>Service fee</span><span>{ngn(fee)}</span>
              </div>
              <div style={{ borderTop: '1px solid #ECE2D8', paddingTop: '0.35rem', display: 'flex', justifyContent: 'space-between', fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.9rem', fontWeight: 800, color: '#1A1A1A' }}>
                <span>You pay</span><span>{ngn(total)}</span>
              </div>
              <p style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.72rem', color: '#7A917A', margin: '0.1rem 0 0', fontWeight: 600 }}>
                The couple receives the full {ngn(amt)} 💛
              </p>
            </div>
          )}

          {error && <p style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.8rem', color: '#C0392B', margin: 0 }}>{error}</p>}

          <button onClick={pay} disabled={busy} className="btn-primary" style={{ width: '100%', justifyContent: 'center', marginTop: '0.25rem' }}>
            {busy ? 'Starting…' : `Continue to payment →`}
          </button>
          <p style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.3rem', fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.68rem', color: '#9B9B9B', margin: 0 }}>
            <Lock size={10} /> Secured payment. The couple receives the full amount you gift.
          </p>
        </div>
      </div>
    </div>
  )
}
